/**
 * Testing State Context
 *
 * Simplified context for episode evaluation. Backend handles the full
 * simulation loop — frontend only stores the result and evaluation.
 *
 * Story: 24.2 - Evolve Single Episode Eval
 */

import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import type {
  EpisodeTestingData,
  SimulationResult,
  EvaluateConversationResponse,
} from '../services/testingApi';

// ============================================================================
// Type Definitions
// ============================================================================

export interface TestingState {
  currentEpisode: EpisodeTestingData | null;
  simulationResult: SimulationResult | null;
  evaluationResults: EvaluateConversationResponse | null;
  selectedPersonaId: number | null;
  personaInstructions: string | null;
}

export interface TestingContextValue extends TestingState {
  setCurrentEpisode: (episode: EpisodeTestingData | null) => void;
  setSimulationResult: (result: SimulationResult | null) => void;
  setEvaluationResults: (results: EvaluateConversationResponse | null) => void;
  setPersona: (personaId: number, instructions: string) => void;
  resetTest: () => void;
}

// ============================================================================
// Context Creation
// ============================================================================

const TestingContext = createContext<TestingContextValue | undefined>(undefined);

// ============================================================================
// Initial State
// ============================================================================

const initialState: TestingState = {
  currentEpisode: null,
  simulationResult: null,
  evaluationResults: null,
  selectedPersonaId: null,
  personaInstructions: null,
};

// ============================================================================
// Provider Component
// ============================================================================

interface TestingProviderProps {
  children: ReactNode;
}

export function TestingProvider({ children }: TestingProviderProps) {
  const [state, setState] = useState<TestingState>(initialState);

  const setCurrentEpisode = useCallback((episode: EpisodeTestingData | null) => {
    setState((prev) => ({ ...prev, currentEpisode: episode }));
  }, []);

  const setSimulationResult = useCallback((result: SimulationResult | null) => {
    setState((prev) => ({ ...prev, simulationResult: result }));
  }, []);

  const setEvaluationResults = useCallback((results: EvaluateConversationResponse | null) => {
    setState((prev) => ({ ...prev, evaluationResults: results }));
  }, []);

  const setPersona = useCallback((personaId: number, instructions: string) => {
    setState((prev) => ({
      ...prev,
      selectedPersonaId: personaId,
      personaInstructions: instructions,
    }));
  }, []);

  const resetTest = useCallback(() => {
    setState((prev) => ({
      ...prev,
      simulationResult: null,
      evaluationResults: null,
    }));
  }, []);

  const value: TestingContextValue = {
    ...state,
    setCurrentEpisode,
    setSimulationResult,
    setEvaluationResults,
    setPersona,
    resetTest,
  };

  return <TestingContext.Provider value={value}>{children}</TestingContext.Provider>;
}

// ============================================================================
// Custom Hook for Consuming Context
// ============================================================================

// eslint-disable-next-line react-refresh/only-export-components
export function useTesting() {
  const context = useContext(TestingContext);
  if (context === undefined) {
    throw new Error('useTesting must be used within a TestingProvider');
  }
  return context;
}
